import React, { useEffect, useRef, useState } from 'react';
import './ChatMessageTimeline.css';
import { ActiveTag, ChatTimelineThinkingMode, Tag } from "../types";
import {
  getAgentFromProgress,
  getAgentFromTransfer,
  getProgressStage,
  getProgressStepId,
  getSummarizationStage,
  getSummarizationId,
  mergeResearchById
} from "./utils";
import { HiOutlineLightBulb, } from 'react-icons/hi';
import TimelineSections from "./TimelineSections";
import ThinkingIndicator from "./ThinkingIndicator";
import ThinkingToggle from "./ThinkingToggle";
import ActiveStepCard from "./ActiveStepCard";
import { useSelector } from "react-redux";

interface ChatMessageTimelineProps {
  tags: Tag[];
  streaming: boolean;
  hasPMTags: boolean;
  isLastMessage: boolean;
  resetExpansion?: number;
}

const ChatMessageTimeline: React.FC<ChatMessageTimelineProps> = ({
  tags,
  streaming = false,
  hasPMTags = false,
  isLastMessage = false,
  resetExpansion = 0
}) => {

  const PROGRESS_END_DELAY_MS = 2500;
  const [isProcessExpanded, setIsProcessExpanded] = useState(false);
  const [completedSteps, setCompletedSteps] = useState<ActiveTag[]>([]);
  const [currentStep, setCurrentStep] = useState<ActiveTag | null>(null);
  const [isApiLoading, setIsApiLoading] = useState(false);
  const [activeStep, setActiveStep] = useState<ActiveTag | null>(null);

  const {
    thinkingMode
  } = useSelector((state: any) => state.chatMessages);

  const [progressEndState, setProgressEndState] = useState<any>({});
  const progressEndTimers = useRef<any>({});

  const trimmedTags = tags.filter(tag => tag?.content.trim());
  const researchMergedTags = mergeResearchById(trimmedTags);
  const hasContent = researchMergedTags.length > 0;

  const toggleProcessExpansion = () => {
    setIsProcessExpanded(prev => !prev);
  }

  useEffect(() => {
    if (resetExpansion > 0) {
      setIsProcessExpanded(false);
    }
  }, [resetExpansion]);

  // Helper function to merge progress and summarization steps
  const mergeProgressSteps = (t: Tag[]) => {
    const progressSteps = new Map<string, ActiveTag>();
    // Track summarization by id - we merge start/end by id, preferring 'end' stage
    const summarizationSteps = new Map<string, ActiveTag>();

    t.forEach((tt, index) => {
      const tag = {
        ...tt,
        content: tt.content
          .replace("<thinking>", "")
          .replace("</thinking>", "")
          .replace("<progress>", "")
          .replace("</progress>", "")
          .replace("<transfer>", "")
          .replace("</transfer>", "")
          .replace("<research>", "")
          .replace("</research>", "")
          .replace("<summarization>", "")
          .replace("</summarization>", "")
      } as any;

      if (tag.type === 'progress') {
        const stepId = getProgressStepId(tag.content);
        const stage = getProgressStage(tag.content);
        const prev = progressSteps.get(stepId);
        if (!prev || stage === 'end' || (prev.stage !== 'end' && prev?.originalIndex && index > prev?.originalIndex)) {
          progressSteps.set(stepId, { ...tag, stepId, stage, originalIndex: index });
        }
      } else if (tag.type === 'summarization') {
        const stage = getSummarizationStage(tag.content);
        const sumId = getSummarizationId(tag.content);
        const prev = summarizationSteps.get(sumId);
        // Prefer 'end' stage, otherwise keep the latest
        if (!prev || stage === 'end' || (prev.stage !== 'end')) {
          const firstIdx = prev?.originalIndex !== undefined ? prev.originalIndex : index;
          summarizationSteps.set(sumId, { ...tag, stepId: sumId, stage, originalIndex: firstIdx });
        }
      }
    });

    const progressArray = Array.from(progressSteps.values())
      .filter(tag => tag.originalIndex !== undefined)
      .sort((a, b) => (a.originalIndex! - b.originalIndex!));

    let progressIdx = 0;
    const summarizationEmitted = new Set<string>();
    const result: ActiveTag[] = [];

    t.forEach((tag) => {
      if (tag.type === 'progress') {
        if (progressIdx < progressArray.length) {
          result.push(progressArray[progressIdx]);
          progressIdx++;
        }
      } else if (tag.type === 'summarization') {
        // Only emit the merged summarization tag once per id (at first occurrence)
        const sumId = getSummarizationId(tag.content);
        if (!summarizationEmitted.has(sumId)) {
          const mergedTag = summarizationSteps.get(sumId);
          if (mergedTag) {
            result.push(mergedTag);
            summarizationEmitted.add(sumId);
          }
        }
        // Skip other summarization tags with same id (they are merged)
      } else {
        result.push(tag as any);
      }
    });

    return result as unknown as Tag[];
  };

  // Apply merge to create actualTags - this merges both progress and summarization tags
  const actualTags = React.useMemo(() => {
    return mergeProgressSteps(researchMergedTags);
  }, [researchMergedTags]);

  useEffect(() => {
    return () => {
      Object.values(progressEndTimers.current)
        .forEach((t: any) => clearTimeout(t));
    };
  }, []);

  useEffect(() => {
    let target: any = currentStep || (completedSteps.length > 0 ? completedSteps[completedSteps.length - 1] : null);

    if (thinkingMode === ChatTimelineThinkingMode.OFF) {
      const merged = mergeProgressSteps(researchMergedTags);
      const lastProgress = [...merged].filter((t: any) => t.type === 'progress').pop();
      if (lastProgress) target = lastProgress as any;
      if (streaming && lastProgress) {
        setCurrentStep(lastProgress as any);
      }
    }

    setActiveStep(target as any);
  }, [currentStep, completedSteps, thinkingMode, researchMergedTags, streaming]);

  useEffect(() => {
    if (!isProcessExpanded) return;
    const mergedTags = isProcessExpanded ? mergeProgressSteps(researchMergedTags) : [];

    mergedTags.forEach((tag) => {
      if (tag.type === 'progress') {
        const stepId = getProgressStepId(tag.content);
        const stage = getProgressStage(tag.content);

        if (stage === 'start' && progressEndState[stepId]) {
          setProgressEndState((prev: any) => {
            const newState = { ...prev };
            delete newState[stepId];
            return newState;
          });

          if (progressEndTimers.current[stepId]) {
            clearTimeout(progressEndTimers.current[stepId]);
            delete progressEndTimers.current[stepId];
          }
        } else if (stage === 'end' && progressEndState[stepId] === undefined) {
          setProgressEndState((prev: any) => ({ ...prev, [stepId]: false }));
          if (!progressEndTimers.current[stepId]) {
            progressEndTimers.current[stepId] = setTimeout(() => {
              setProgressEndState((prev: any) => ({ ...prev, [stepId]: true }));
              delete progressEndTimers.current[stepId];
            }, streaming ? PROGRESS_END_DELAY_MS : 10);
          }
        }
      }
    });
  }, [researchMergedTags, isProcessExpanded, streaming]);

  useEffect(() => {
    if (streaming || tags.length > 0) {
      setIsApiLoading(tags.length === 0); // Show "Thinking..." only when no tags yet

      // Use merged tags for consistent behavior
      const mergedTagsForState = mergeProgressSteps(researchMergedTags);

      // Track completed and current steps
      const completed = mergedTagsForState.filter(tag => (tag as any)?.content?.trim && (tag as any)?.content.trim() && !(tag as any).isStreaming);
      const current = mergedTagsForState.find((tag: any) => tag.isStreaming || (tag === mergedTagsForState[mergedTagsForState.length - 1] && (tag as any).content));

      let enhancedCurrent: any = current;
      if (current && current.type === 'transfer') {
        const agent = getAgentFromTransfer((current as any).content) || '';
        const following: Tag[] = [] as any;
        const idx = mergedTagsForState.indexOf(current as any);
        for (let k = idx + 1; k < mergedTagsForState.length; k++) {
          const t: any = mergedTagsForState[k];
          if (t.type !== 'progress') break;
          const pAgent = getAgentFromProgress(t.content);
          if (agent && pAgent === agent) {
            following.push(t);
          } else {
            break;
          }
        }
        const lastTag = researchMergedTags[researchMergedTags.length - 1] as any;
        if (lastTag && lastTag.type === 'progress' && getAgentFromProgress(lastTag.content) === agent) {
          const lastId = getProgressStepId(lastTag.content);
          const exists = following.some((t: any) => getProgressStepId(t.content) === lastId);
          if (!exists) following.push(lastTag);
        }
        enhancedCurrent = { ...(current as any), groupedProgress: following };
      } else if (current && current.type === 'progress') {

        const agent = getAgentFromProgress((current as any).content) || '';
        const idx = mergedTagsForState.indexOf(current as any);
        let transferIdx = -1;
        for (let k = idx - 1; k >= 0; k--) {
          const t: any = mergedTagsForState[k];
          if (t.type === 'transfer') {
            const tAgent = getAgentFromTransfer(t.content);
            if (tAgent && agent && tAgent === agent) {
              transferIdx = k;
            }
            break; // stop on first transfer encountered
          }
        }
        if (transferIdx !== -1) {
          const transferTag: any = mergedTagsForState[transferIdx];
          const following: Tag[] = [] as any;
          for (let k = transferIdx + 1; k < mergedTagsForState.length; k++) {
            const t: any = mergedTagsForState[k];
            if (t.type !== 'progress') break;
            const pAgent = getAgentFromProgress(t.content);
            if (agent && pAgent === agent) {
              following.push(t);
            } else {
              break;
            }
          }
          const lastTag = researchMergedTags[researchMergedTags.length - 1] as any;
          if (lastTag && lastTag.type === 'progress' && getAgentFromProgress(lastTag.content) === agent) {
            const lastId = getProgressStepId(lastTag.content);
            const exists = following.some((t: any) => getProgressStepId(t.content) === lastId);
            if (!exists) following.push(lastTag);
          }
          enhancedCurrent = { ...transferTag, groupedProgress: following };
        }
      }

      setCompletedSteps(completed as any);
      setCurrentStep(enhancedCurrent as any || null);
    } else {
      setIsApiLoading(false);
      setCurrentStep(null);
    }
  }, [streaming, tags]);

  if (!tags || tags.length === 0) {
    // Show "Thinking..." when API is being hit but no content yet
    if (streaming || isApiLoading) {
      // If thinking is hidden, only show a minimal loading state
      if (thinkingMode === ChatTimelineThinkingMode.OFF) {
        return (
          <div className="tags-timeline-container">
            <div className="thinking-loader">
              <span className="thinking-text thinking-text-glow">Processing...</span>
              <div className="thinking-dots">
                <div className="thinking-dot"></div>
                <div className="thinking-dot"></div>
                <div className="thinking-dot"></div>
              </div>
            </div>
          </div>
        );
      }

      return (
        <div className="tags-timeline-container">
          <div className="thinking-loader">
            <HiOutlineLightBulb className="thinking-icon" />
            <span className="thinking-text thinking-text-glow">Thinking</span>
            <div className="thinking-dots">
              <div className="thinking-dot"></div>
              <div className="thinking-dot"></div>
              <div className="thinking-dot"></div>
            </div>

          </div>
        </div>
      );
    }
    return null;
  }

  if (!hasContent && !streaming) {
    return null;
  }

  return (
    <div className="tags-timeline-container">
      <div
        className="processing-time-indicator cursor-pointer"
        onClick={toggleProcessExpansion}
      >
        <ThinkingIndicator
          activeStep={activeStep}
          currentStep={currentStep}
          isLastMessage={isLastMessage}
          isProcessExpanded={isProcessExpanded}
          streaming={streaming}
          hasPMTags={hasPMTags}
        />
      </div>

      {isLastMessage && streaming && thinkingMode === ChatTimelineThinkingMode.COMPACT && !hasPMTags && !isProcessExpanded && (
        <div className="collapsed-steps-view">
          <ActiveStepCard
            activeStep={activeStep}
            currentStep={currentStep}
            actualTags={actualTags}
            progressEndState={progressEndState}
            toggleProcessExpansion={toggleProcessExpansion}
          />
        </div>
      )}

      {isProcessExpanded && (
        <div className="relative tags-wrapper !ml-[12px]">
          <ThinkingToggle />

          <TimelineSections
            actualTags={actualTags}
            progressEndState={progressEndState}
          />
        </div>
      )}
    </div>
  );
};

export default ChatMessageTimeline;